import React, { Component } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PropTypes from 'prop-types';
import { Clipboard, Check, Download } from 'react-feather';
import QRCode from 'qrcode.react';

import Card from '../common/card';
import BSFormControl from '../common/form/bsFormControl';

import { getVoterPreviewUrl } from '../../helpers/utils';
import { LINK_SOURCES_MAX_COUNT } from '../../helpers/topicBuilderHelper';
import { isOnMobile } from '../../helpers/utils';
import { CopyToClipboard } from 'react-copy-to-clipboard';

const QR_IMAGE_SIZE_OPTIONS = [
	{ label: '1024x1024px', value: 1024 },
	{ label: '512x512px', value: 512 },
	{ label: '256x256px', value: 256 },
	{ label: '128x128px', value: 128 }
];

const QR_DOWNLOAD_FRAME_RATIO = 0.06;
const QR_DOWNLOAD_INITIAL_SIZE = 256;
const QR_DOWNLOAD_MINIMAL_SIZE_WITH_LOGO = 256;

class TopicMediaEmbed extends Component {
	constructor(props) {
		super(props);
		this.state = {
			urlCopied: false,
			htmlSnippetCopied: false,
			selectedImageSizeOption: QR_DOWNLOAD_INITIAL_SIZE,
			selectedLinkSource: ''
		};

		this.mediaKit = React.createRef();

		this.selectedQRsize = QR_DOWNLOAD_INITIAL_SIZE * ( 1 - 2 * QR_DOWNLOAD_FRAME_RATIO );

		this.fillBackground = this.fillBackground.bind(this);
		this.fillQRinCanvas = this.fillQRinCanvas.bind(this);
		this.fillLogoInQR = this.fillLogoInQR.bind(this);

		this.handleImageSizeSelection = this.handleImageSizeSelection.bind(this);
		this.handleDownloadClick = this.handleDownloadClick.bind(this);
		this.setStateCopied = this.setStateCopied.bind(this);
		this.handleLinkSourceSelection = this.handleLinkSourceSelection.bind(this);
		this.resetQRCodePreviews = this.resetQRCodePreviews.bind(this);
		this.qrImg = document.createElement('img');
		this.handleQrImageLoad = this.handleQrImageLoad.bind(this);
	}

	componentDidMount() {
		this.resetQRCodePreviews();
		this.mediaKit.current.getElementsByClassName('html-snippet-preview')[0].value = this.getHtmlSnippetCode();
		this.qrImg.addEventListener('load', this.handleQrImageLoad);
	}
	
	componentWillUnmount() {
		this.qrImg.removeEventListener('load', this.handleQrImageLoad);
	}

	handleQrImageLoad(event) {
		const sizeCanvas = this.state.selectedImageSizeOption;
		const sizeQRtoDownload = this.generatedQRcodeCanvas.width;
		const position = this.QRbackgroundFrameWidth;
		this.contextCanvasToDownload.drawImage(
			event.target,
			0,
			0,
			sizeQRtoDownload,
			sizeQRtoDownload,
			position,
			position,
			this.selectedQRsize,
			this.selectedQRsize
		);
		if (sizeCanvas >= QR_DOWNLOAD_MINIMAL_SIZE_WITH_LOGO) {
			this.fillLogoInQR(this.fullCanvasToDownload);
		}
	}

	get QRbackgroundFrameWidth() {
		return QR_DOWNLOAD_FRAME_RATIO * this.state.selectedImageSizeOption;
	}

	handleDownloadClick(){
		const link = document.createElement('a');

		// polyfill for Edge support
		if (!this.fullCanvasToDownload.toBlob) {
			Object.defineProperty(this.fullCanvasToDownload, 'toBlob', {
				value: ( callback ) => {
					var dataURL = this.dataCanvasToDownload.split(',')[1];
					setTimeout(function() {
						var binaryString = atob( dataURL ),
						binaryArray = new Uint8Array(binaryString.length);

						for (var i = 0; i < binaryString.length; i++ ) {
							binaryArray[i] = binaryString.charCodeAt(i);
						}

						callback(new Blob( [binaryArray], { type: 'image/png' } ));
					});
				}
			});
		}

		this.fullCanvasToDownload.toBlob(( blobToDownload ) => {
			link.href = window.URL.createObjectURL(blobToDownload);
			link.download = `${this.props.topicName}_${this.state.selectedImageSizeOption}px_qr.png`;
			
			const evt = new MouseEvent('click', {
				view: window
			});
	
			link.dispatchEvent(evt);
		});
	}

	handleImageSizeSelection(ev){
		this.setState({ selectedImageSizeOption: Number(ev.target.value) }, this.fillCanvasForDownload);
	}

	handleLinkSourceSelection(ev) {
		this.setState({ selectedLinkSource: ev.target.value }, this.resetQRCodePreviews);
	}

	resetQRCodePreviews() {
		this.fillLogoInQR(this.canvasQRPreview, 192);
		this.fillCanvasForDownload();
	}

	fillCanvasForDownload() {
		this.fillBackground();
		this.fillQRinCanvas();
	}

	fillBackground() {
		this.contextCanvasToDownload.fillStyle = '#FFFFFF';
		this.contextCanvasToDownload.fillRect(
			0,
			0,
			this.fullCanvasToDownload.width,
			this.fullCanvasToDownload.height
		);
	}

	fillQRinCanvas() {
		this.qrImg.src = this.qrData;
	}

	fillLogoInQR(canvasToFill, spaceToFillSize) {
		const position = spaceToFillSize ? spaceToFillSize * 0.4 : canvasToFill.width * 0.4;
		
		const logo = document.createElement('img');
		const canvasSize = spaceToFillSize ? spaceToFillSize : this.state.selectedImageSizeOption;
		logo.src = this.getLogoData(canvasSize);

		logo.onload = () => {
			canvasToFill.getContext('2d').drawImage(
				logo,
				position,
				position);
		};
	}

	get fullCanvasToDownload() {
		return document.getElementById('imageToDownload');
	}

	get generatedQRcodeCanvas() {
		return document.getElementById('QRcodeToFill');
	}

	get canvasQRPreview() {
		return document.getElementById('QRPreview');
	}

	get contextCanvasToDownload() {
		return document.getElementById('imageToDownload').getContext('2d');
	}

	get dataCanvasToDownload() {
		return  document.getElementById('imageToDownload').toDataURL('image/png');
	}

	get qrData() {
		return  document.getElementById('QRcodeToFill').toDataURL('image/png');
	}

	getLogoData(sizeCanvas) {
		switch (sizeCanvas) {
			case 192:
				// used in qr-preview only:
				return '/images/logo32.png';
			case 256:
				return '/images/logo48.png';
			case 512:
				return '/images/logo96.png';
			case 1024:
				return '/images/logo192.png';
			default:
				return null;
		}
	}

	getImageSizeOptions(){
		return QR_IMAGE_SIZE_OPTIONS.map((o, i) => <option key={i} value={o.value}>{o.label}</option>);
	}
	
	getLinkSourceOptions() {
		return this.props.linkSources.map((ls, i) => <option key={i} value={ls.value}>
			{ls.value}
		</option>);
	}

	setStateCopied(copied) {
		switch(copied) {
			case 'urlCopied':
				this.setState({ urlCopied: true, htmlSnippetCopied: false });
				break;
			case 'htmlSnippetCopied':
				this.setState({ htmlSnippetCopied: true, urlCopied: false });
				break;
			default:
				break;
		}
	}

	htmlSnippetTemplate(topicName, topicUrl) {
		const topicUrlWithSource = this.appendEmbeddedHtmlSource(topicUrl);
		const imgHollerBotfaceSrc = `${this.getHollerBotface()}#${topicName}_${Date.now()}`;

		return <table style={{ borderSpacing: "6px", borderCollapse: "separate", fontFamily: "Arial, Helvetica, Roboto, sans-serif" }}>
			<tbody>
				<tr>
					<td style={{ width: "16.66666667%", textAlign: "center" }}>
						<img src={`${imgHollerBotfaceSrc}`}
							height="54px"
							width="60px"
							alt="Holler Live vote"></img>
					</td>
					<td style={{ width: "83.33333333%", textAlign: "left" }} >
						<p style={{ marginBottom: '5px', fontWeight: 'bold' }}> {topicName.toLowerCase().includes('how do you feel about') ? `${topicName}?` : `How do you feel about ${topicName}?`} </p>
						<p style={{ marginBottom: '10px'}}> Tell us with a swipe of your finger, no installation. </p>
						<a target={"_blank"}
							style={{
								textDecoration: 'none',
								textTransform: 'uppercase',
								padding: '4px 8px',
								fontWeight: 'bold',
								fontSize: '18px',
								lineHeight: '1.333333',
								borderRadius: '6px',
								color: '#fff',
								backgroundColor: '#11A509',
								borderColor: '#4cae4c',
								border: '1px solid transparent',
								textAlign: 'center',
								cursor: 'pointer',
								verticalAlign: 'middle',
								whiteSpace: 'nowrap',
								overflow: 'visible' }}
							href={`${topicUrlWithSource}`}
						>
							let's go!
						</a>
					</td>
				</tr>
			</tbody>
		</table>;
	}

	getHollerBotface() {
		const env = process.env.REACT_APP_DEPLOY_ENV || 'localdev';

		switch (env) {
			case 'production' : {
				return "https://img.holler.live/images/logo_anim_smiley.gif";
			}
			case 'staging' : {
				return "https://imgs.holler.live/images/logo_anim_smiley.gif";
			}
			case 'localdev' : {
				return "https://imgs.holler.live/images/logo_anim_smiley.gif";
			}
			default : {
				return "https://img.holler.live/images/logo_anim_smiley.gif";
			}
		}
	}
	
	getHtmlSnippetCode() {
		return document.getElementById('html-snippet-container').innerHTML;
	}

	appendQrcodeSource(topicUrl) {
		const sourcesCount = (topicUrl.match(/[?&]s=/g) || []).length;
		const defaultSourcePresent = (topicUrl.match(/[?&]s=qrcode/g) || []).length > 0;
		if (sourcesCount < LINK_SOURCES_MAX_COUNT && !defaultSourcePresent) {
			if(sourcesCount === 0) {
				return `${topicUrl}?s=qrcode`;
			}
			return `${topicUrl}&s=qrcode`;
		}
		return topicUrl;
	}

	appendEmbeddedHtmlSource(topicUrl) {
		const sourcesCount = (topicUrl.match(/[?&]s=/g) || []).length;
		if(sourcesCount === 0) {
			return `${topicUrl}?s=embeddedSnippet`;
		}
		return `${topicUrl}&s=embeddedSnippet`;
	}

	render() {
		const { topicName, topicDisplayName } = this.props;

		const topicNameLabel = topicDisplayName && topicDisplayName.length > 0 ? topicDisplayName : topicName;
		const topicUrl = getVoterPreviewUrl(topicName, this.state.selectedLinkSource);
		const topicQrUrl = this.appendQrcodeSource(topicUrl);
		this.selectedQRsize = this.state.selectedImageSizeOption - (this.QRbackgroundFrameWidth * 2);

		return (
			<div className="topic-links-modal-content" ref={this.mediaKit}>
				<p className="text-label text-light">
					view topic <strong>{`"${topicNameLabel}"`}</strong><br />
					view topic on mobile, by scanning the qr code
				</p>

				{this.props.linkSources && this.props.linkSources.length > 0 &&
					<form className="form-horizontal">
						<div className="formGroup row-fluid clearfix">
							<div className="col-md-9">
								<BSFormControl labelColWidth={5} label="Link Source" horizontal>
									<select className="form-control uitest-linksource-select" value={this.state.selectedLinkSource} onChange={this.handleLinkSourceSelection} >
										<option value=''>no sources</option>
										{this.getLinkSourceOptions()}
									</select>
								</BSFormControl>
							</div>
						</div>
					</form>
				}

				<Card>
					<QRCode id="QRPreview" size={192} value={topicQrUrl} level='Q' className="uitest-qrcode" />
				</Card>

				<form className="form-horizontal">
					<div className="form-group row-fluid clearfix">
						<div className="col-md-7">
							<BSFormControl labelColWidth={7} label="Image Size" horizontal>
								<select className="form-control" value={this.state.selectedImageSizeOption} onChange={this.handleImageSizeSelection}>
									{this.getImageSizeOptions()}
								</select>
							</BSFormControl>
						</div>
						<div className="col-md-2">
							<div className="form-group">
								<button type="button" className="btn btn-link download uitest-btn-download" onClick={this.handleDownloadClick}><Download size={18} /> download</button>
							</div>
						</div>
					</div>

					<p className="text-label text-light">or open topic in url in your browser:</p>
					<a className="topic-url text-black" href={`${topicUrl}`} target="_blank" rel="noopener noreferrer">{topicUrl}</a>
					<CopyToClipboard text={ topicUrl }
													 onCopy={ () => this.setStateCopied('urlCopied') }>
						<button type="button" className="btn btn-link copy-to-clipboard text-light text-label">
							<Clipboard size={ 14 }/> copy url to clipboard
							{ this.state.urlCopied && <Check size={ 12 }/> }
						</button>
					</CopyToClipboard>
					

					<div className="embeddable-html-snippet">
						<p className="text-label text-light">embeddable html snippet for selected sources</p>
						<Card className="row-fluid clearfix html-snippet-card">
							<div className={`col-md-6 ${isOnMobile() ? 'col-xs-12' : ''}`}>
								<label className="text-label font-weight-bold">embeddable snippet preview</label>
								<div id="html-snippet-container">
									{this.htmlSnippetTemplate(topicNameLabel, topicUrl)}
								</div>
							</div>

							<div className={`col-md-6 ${isOnMobile() ? 'col-xs-12' : ''}`}>
								<label className="text-label font-weight-bold">html code snippet</label>
								<CopyToClipboard text={ renderToStaticMarkup(this.htmlSnippetTemplate(topicNameLabel, topicUrl)) }
																 onCopy={ () => this.setStateCopied('htmlSnippetCopied') }>
									<button type="button" className="btn btn-link copy-to-clipboard text-light text-label">
										<Clipboard size={ 14 }/> copy to clipboard
										{ this.state.htmlSnippetCopied && <Check size={ 12 }/> }
									</button>
								</CopyToClipboard>

								<div className="html-snippet-preview-box">
									<textarea
										className="html-snippet-preview"
										type="text"
										disabled={true}
									/>
								</div>
							</div>
						</Card>
					</div>


					<div id="generatedQRtoDownload" className="hidden-element">
						<canvas id="imageToDownload" width={ this.state.selectedImageSizeOption } height={ this.state.selectedImageSizeOption } />
						<QRCode id="QRcodeToFill" value={topicQrUrl} size={this.selectedQRsize} width={this.selectedQRsize} height={this.selectedQRsize} level='M'
							style={{ width: `${this.selectedQRsize}px`, height: `${this.selectedQRsize}px` }} className="uitest-qrcode-download" />
					</div>
				</form>
			</div>
		);
	}
}

TopicMediaEmbed.propTypes = {
	topicName: PropTypes.string.isRequired,
	topicDisplayName: PropTypes.string,
	linkSources: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.number,
		value: PropTypes.string
	}))
};

export default TopicMediaEmbed;